/**
 * Trial balance — account balances as of a date, rendered as a COLLAPSIBLE
 * hierarchy (parent accounts → sub-accounts) with expand/collapse arrows so you
 * can see which account sits inside which. Parent rows roll up their own postings
 * plus every descendant; the grand total sums the roots (each leaf counted once),
 * so the debit/credit balance check is unchanged.
 */
import type { TrialBalanceRow } from '@erp/shared';
import Decimal from 'decimal.js';
import { AlertTriangle, CheckCircle2, ChevronDown, Scale } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Badge,
  Card,
  Input,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/shadcn/index.js';
import { Button, EmptyState } from '../../components/ui/index.js';
import { useLayoutDirection } from '../../hooks/useDirection.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { formatMoney } from '../../lib/money.js';

interface TreeNode {
  row: TrialBalanceRow;
  depth: number;
  children: TreeNode[];
  rollDebit: Decimal;
  rollCredit: Decimal;
  rollBalance: Decimal;
  /** Balance per TRANSACTION currency, descendants rolled in. */
  rollByCcy: Map<string, Decimal>;
}

/** Build a parent→child forest from the flat rows, rolling debit/credit up. */
function buildTree(rows: TrialBalanceRow[]): { roots: TreeNode[]; parentIds: string[] } {
  const nodes = new Map<string, TreeNode>();
  for (const row of rows) {
    nodes.set(row.accountId, { row, depth: 0, children: [], rollDebit: new Decimal(0), rollCredit: new Decimal(0), rollBalance: new Decimal(0), rollByCcy: new Map() });
  }
  const roots: TreeNode[] = [];
  for (const node of nodes.values()) {
    const parent = node.row.parentId ? nodes.get(node.row.parentId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node); // null parent, or parent outside this set (orphan-safe)
  }
  const byCode = (a: TreeNode, b: TreeNode): number => a.row.accountCode.localeCompare(b.row.accountCode);
  const parentIds: string[] = [];
  // Post-order: assign depth, sort children, and roll debit/credit up the tree.
  const visit = (node: TreeNode, depth: number): void => {
    node.depth = depth;
    node.children.sort(byCode);
    if (node.children.length) parentIds.push(node.row.accountId);
    let debit = new Decimal(node.row.baseDebit);
    let credit = new Decimal(node.row.baseCredit);
    const byCcy = new Map<string, Decimal>();
    for (const c of node.row.byCurrency ?? []) byCcy.set(c.currencyCode, new Decimal(c.balance));
    for (const child of node.children) {
      visit(child, depth + 1);
      debit = debit.plus(child.rollDebit);
      credit = credit.plus(child.rollCredit);
      for (const [k, v] of child.rollByCcy) byCcy.set(k, (byCcy.get(k) ?? new Decimal(0)).plus(v));
    }
    node.rollByCcy = byCcy;
    node.rollDebit = debit;
    node.rollCredit = credit;
    const isDebitNormal = node.row.accountType === 'asset' || node.row.accountType === 'expense';
    node.rollBalance = isDebitNormal ? debit.minus(credit) : credit.minus(debit);
  };
  roots.sort(byCode);
  for (const root of roots) visit(root, 0);
  return { roots, parentIds };
}

/** True when `to` is the last calendar day of `from`'s month. */
function setMonthMatches(from: string, to: string): boolean {
  const [y, mo] = from.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y!, mo!, 0)).getUTCDate();
  return to === `${from.slice(0, 7)}-${String(lastDay).padStart(2, '0')}`;
}

export function TrialBalancePage(): JSX.Element {
  const { t, i18n } = useTranslation('gl');
  const tenant = useAuthStore((s) => s.tenant);
  const baseCurrency = tenant?.baseCurrencyCode ?? 'USD';
  const locale = i18n.language;
  const dir = useLayoutDirection();

  // Range: `from` empty = cumulative from the beginning (the report's old and
  // default meaning); both set = only the window's own movement. The month
  // input and the preset chips are just fast hands on the same two dials.
  const [from, setFrom] = useState('');
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState<TrialBalanceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // 'base' = debit/credit/balance in the base currency (the balance check);
  // 'ccy' = each account's balance in every currency it moved in.
  const [view, setView] = useState<'base' | 'ccy'>('base');

  useEffect(() => {
    setError(null);
    setRows(null);
    // Typed-in reversal (from after to) queries as the user surely meant it,
    // not as an empty window.
    const lo = from && from > asOf ? asOf : from;
    const hi = from && from > asOf ? from : asOf;
    const q = new URLSearchParams({ asOf: hi });
    if (lo) q.set('from', lo);
    void api()
      .get<{ items: TrialBalanceRow[] }>(`/general-ledger/trial-balance?${q.toString()}`)
      .then((r) => setRows(r.data.items))
      .catch((e) => setError(String((e as Error).message ?? e)));
  }, [from, asOf]);

  /** First and last calendar day of a YYYY-MM month, without timezone drift. */
  const setMonth = (ym: string): void => {
    if (!/^\d{4}-\d{2}$/.test(ym)) return;
    const [y, mo] = ym.split('-').map(Number);
    const lastDay = new Date(Date.UTC(y!, mo!, 0)).getUTCDate();
    setFrom(`${ym}-01`);
    setAsOf(`${ym}-${String(lastDay).padStart(2, '0')}`);
  };
  const today = new Date().toISOString().slice(0, 10);
  const thisMonth = today.slice(0, 7);
  const lastMonthOf = (ym: string): string => {
    const [y, mo] = ym.split('-').map(Number);
    const d = new Date(Date.UTC(y!, mo! - 2, 1));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  };
  /** Which YYYY-MM the current range IS, or '' when it isn't exactly one month. */
  const activeMonth = ((): string => {
    if (!from || from.slice(0, 7) !== asOf.slice(0, 7) || !from.endsWith('-01')) return '';
    return setMonthMatches(from, asOf) ? from.slice(0, 7) : '';
  })();
  const isAllTime = !from && asOf === today;
  const preset = (f: string, to: string): void => { setFrom(f); setAsOf(to); };

  const { roots, parentIds } = useMemo(() => (rows ? buildTree(rows) : { roots: [], parentIds: [] }), [rows]);
  // Currencies anything moved in — base first — one column each in the per-currency view.
  const currencies = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows ?? []) for (const c of r.byCurrency ?? []) set.add(c.currencyCode);
    return [...set].sort((a, b) => (a === baseCurrency ? -1 : b === baseCurrency ? 1 : a.localeCompare(b)));
  }, [rows, baseCurrency]);
  const byCcyView = view === 'ccy' && currencies.length > 0;

  // Flatten the tree to the rows currently visible (parent expanded ⇒ children shown).
  const visible = useMemo(() => {
    const out: TreeNode[] = [];
    const walk = (node: TreeNode): void => {
      out.push(node);
      if (!collapsed.has(node.row.accountId)) node.children.forEach(walk);
    };
    roots.forEach(walk);
    return out;
  }, [roots, collapsed]);

  const totals = useMemo(() => {
    let debit = new Decimal(0);
    let credit = new Decimal(0);
    for (const root of roots) { debit = debit.plus(root.rollDebit); credit = credit.plus(root.rollCredit); }
    return { debit: debit.toFixed(4), credit: credit.toFixed(4), variance: debit.minus(credit) };
  }, [roots]);
  const isBalanced = totals.variance.isZero();

  const toggle = (id: string): void =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const expandAll = (): void => setCollapsed(new Set());
  const collapseAll = (): void => setCollapsed(new Set(parentIds));
  const m = (v: Decimal | string): string => formatMoney(typeof v === 'string' ? v : v.toFixed(4), { locale, currency: baseCurrency });
  const mc = (v: Decimal | undefined, ccy: string): string =>
    v && !v.isZero() ? formatMoney(v.toFixed(4), { locale, currency: ccy }) : '—';

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">{t('trialBalance.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('trialBalance.description')}</p>
        </div>
        {currencies.length > 1 && (
          <div className="inline-flex gap-1 rounded-lg border border-border p-0.5" role="tablist">
            {(['base', 'ccy'] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                onClick={() => setView(v)}
                className={`rounded-md px-3 py-1 text-xs font-semibold ${view === v ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:bg-bg-subtle'}`}
              >
                {v === 'base' ? t('trialBalance.viewBase', { ccy: baseCurrency }) : t('trialBalance.viewByCurrency')}
              </button>
            ))}
          </div>
        )}
        {parentIds.length > 0 && (
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={expandAll}>{t('trialBalance.expandAll', { defaultValue: 'Expand all' })}</Button>
            <Button variant="ghost" size="sm" onClick={collapseAll}>{t('trialBalance.collapseAll', { defaultValue: 'Collapse all' })}</Button>
          </div>
        )}
      </header>

      <Card className="p-3">
        <div className="flex flex-wrap items-end gap-x-4 gap-y-3">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-fg-muted">{t('trialBalance.fromLabel', { defaultValue: 'From' })}</span>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-fg-muted">{t('trialBalance.toLabel', { defaultValue: 'To' })}</span>
            <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-40" />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <span className="text-fg-muted">{t('trialBalance.monthLabel', { defaultValue: 'Month' })}</span>
            {/* One tap for "show me July": sets from/to to the month's edges. */}
            <Input type="month" value={activeMonth} onChange={(e) => setMonth(e.target.value)} className="w-44" />
          </label>
          <div className="flex flex-wrap items-center gap-1.5">
            {([
              ['thisMonth', (): void => setMonth(thisMonth), activeMonth === thisMonth],
              ['lastMonth', (): void => setMonth(lastMonthOf(thisMonth)), activeMonth === lastMonthOf(thisMonth)],
              ['thisYear', (): void => preset(`${today.slice(0, 4)}-01-01`, today), from === `${today.slice(0, 4)}-01-01` && asOf === today],
              ['allTime', (): void => preset('', today), isAllTime],
            ] as const).map(([key, run, active]) => (
              <button
                key={key}
                type="button"
                onClick={run}
                className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  active
                    ? 'border-brand bg-brand/10 text-brand'
                    : 'border-border bg-bg-subtle text-fg-muted hover:text-fg'
                }`}
              >
                {t(`trialBalance.presets.${key}`, {
                  defaultValue: key === 'thisMonth' ? 'This month' : key === 'lastMonth' ? 'Last month' : key === 'thisYear' ? 'This year' : 'All time',
                })}
              </button>
            ))}
          </div>
          {from && (
            <p className="basis-full text-xs text-fg-muted">
              {t('trialBalance.rangeHint', { defaultValue: 'Showing only the movement between the two dates — not opening balances.' })}
            </p>
          )}
        </div>
      </Card>

      {error ? (
        <Card className="border-danger/40 bg-danger/5 p-4">
          <div className="flex items-start gap-2 text-sm text-danger" role="alert">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden />
            <div className="flex-1">
              <p className="font-medium">{t('trialBalance.loadFailed')}</p>
              <p className="mt-0.5 text-xs text-danger/80">{error}</p>
            </div>
          </div>
        </Card>
      ) : !rows ? (
        <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Scale className="h-8 w-8" aria-hidden />}
          title={t('trialBalance.empty')}
          description={t('trialBalance.emptyDescription')}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <div
            className={`flex items-center gap-2 border-b px-4 py-2.5 text-sm ${
              isBalanced
                ? 'border-success/30 bg-success/5 text-success'
                : 'border-danger/30 bg-danger/5 text-danger'
            }`}
            role="status"
            aria-live="polite"
          >
            {isBalanced ? (
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" aria-hidden />
            ) : (
              <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden />
            )}
            <span className="font-medium">
              {isBalanced
                ? t('trialBalance.balanced')
                : t('trialBalance.outOfBalance', { amount: m(totals.variance.abs()) })}
            </span>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('trialBalance.columns.code')}</TableHead>
                <TableHead>{t('trialBalance.columns.name')}</TableHead>
                <TableHead>{t('trialBalance.columns.type')}</TableHead>
                {byCcyView ? (
                  <>
                    {currencies.map((c) => (
                      <TableHead key={c} className="text-end">{t('trialBalance.columns.balanceIn', { ccy: c })}</TableHead>
                    ))}
                    <TableHead className="text-end">{t('trialBalance.columns.balanceIn', { ccy: baseCurrency })} ≈</TableHead>
                  </>
                ) : (
                  <>
                    <TableHead className="text-end">{t('trialBalance.columns.debit')}</TableHead>
                    <TableHead className="text-end">{t('trialBalance.columns.credit')}</TableHead>
                    <TableHead className="text-end">{t('trialBalance.columns.balance')}</TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((node) => {
                const hasChildren = node.children.length > 0;
                const isCollapsed = collapsed.has(node.row.accountId);
                return (
                  <TableRow key={node.row.accountId} className={hasChildren ? 'font-medium' : undefined}>
                    <TableCell className="font-mono text-xs">
                      <div className="flex items-center gap-1.5" style={{ paddingInlineStart: `${node.depth * 1.15}rem` }}>
                        {hasChildren ? (
                          <button
                            type="button"
                            onClick={() => toggle(node.row.accountId)}
                            className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-fg-muted hover:bg-bg-muted hover:text-fg"
                            aria-label={isCollapsed ? t('trialBalance.expand', { defaultValue: 'Expand' }) : t('trialBalance.collapse', { defaultValue: 'Collapse' })}
                            aria-expanded={!isCollapsed}
                          >
                            <ChevronDown
                              className={`h-4 w-4 transition-transform ${isCollapsed ? (dir === 'rtl' ? 'rotate-90' : '-rotate-90') : ''}`}
                              aria-hidden
                            />
                          </button>
                        ) : (
                          <span className="inline-block h-5 w-5 flex-shrink-0" aria-hidden />
                        )}
                        <span>{node.row.accountCode}</span>
                      </div>
                    </TableCell>
                    <TableCell className={hasChildren ? 'font-semibold' : 'font-medium'}>{node.row.accountName}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{t(`type.${node.row.accountType}`)}</Badge>
                    </TableCell>
                    {byCcyView ? (
                      <>
                        {currencies.map((c) => (
                          <TableCell key={c} className="text-end font-mono font-semibold tabular-nums" dir="ltr">{mc(node.rollByCcy.get(c), c)}</TableCell>
                        ))}
                        <TableCell className="text-end font-mono tabular-nums text-fg-muted" dir="ltr">{m(node.rollBalance)}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="text-end font-mono tabular-nums">{m(node.rollDebit)}</TableCell>
                        <TableCell className="text-end font-mono tabular-nums">{m(node.rollCredit)}</TableCell>
                        <TableCell className="text-end font-mono font-semibold tabular-nums">{m(node.rollBalance)}</TableCell>
                      </>
                    )}
                  </TableRow>
                );
              })}
              {byCcyView ? (
                <TableRow className="bg-bg-muted hover:bg-bg-muted">
                  <TableCell className="text-xs text-fg-muted" colSpan={3 + currencies.length + 1}>
                    {t('trialBalance.byCurrencyNote', { ccy: baseCurrency })}
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow className="bg-bg-muted hover:bg-bg-muted">
                  <TableCell className="font-semibold" colSpan={3}>{t('trialBalance.totals')}</TableCell>
                  <TableCell className="text-end font-mono font-semibold tabular-nums">{m(totals.debit)}</TableCell>
                  <TableCell className="text-end font-mono font-semibold tabular-nums">{m(totals.credit)}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
